import { DataFrame } from '@lib/mobx/dataframe';
import { api, ApiRequestInit } from '@lib/network/api';
import { resolveUserBillingId } from '@lib/utils/billingOwnership';
import { INGEST_CREDITS_PER_HOUR, type IngestEstimate } from '@lib/utils/ingestEstimate';
import {
  AFFORDABILITY_UNKNOWN,
  affordabilityFromError,
  OwnerAffordability,
} from '@lib/utils/ownerAffordability';
import { showToast } from '@lib/utils/toastmaster';
import { computed, extendObservable, reaction, runInAction } from 'mobx';
import { userStore } from './User';

// How much in credits does it cost per hour of ingested media.
export const COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS = INGEST_CREDITS_PER_HOUR;

// How much in credits does it cost per minute to remove watermarks.
export const COST_WATERMARK_REMOVAL_PER_MINUTE_IN_CREDITS = 250;

// Approximate cost in API credits for a single Agent message/interaction.
export const COST_APPROXIMATE_AGENT_MESSAGE_IN_CREDITS = 10_000;

// --- Display-only per-action credit costs (pricing page) ---
// These mirror the real per-action credit costs (StarZero API + agent service) so the
// pricing page can show what a plan's credits convert to. They are NOT used for billing
// logic — the backend is the source of truth — so keep them in sync when rates change.

// AI image generation floor (real images often cost more than this minimum).
export const COST_APPROXIMATE_IMAGE_FLOOR_IN_CREDITS = 2_400;

// AI video generation, per fixed-length (8s) clip — "fast" vs "cinematic" quality.
export const COST_VIDEO_FAST_PER_CLIP_IN_CREDITS = 24_000;
export const COST_VIDEO_CINEMATIC_PER_CLIP_IN_CREDITS = 64_000;

// Music generation, per minute of output.
export const COST_MUSIC_PER_MINUTE_IN_CREDITS = 3_000;

// AI voiceover (TTS): ~1,800 credits / minute of speech (2 cr/char x ~900 chars/min at ~150 wpm).
export const COST_VOICEOVER_PER_MINUTE_IN_CREDITS = 1_800;

// Sound effects, per generated effect (auto duration = 5s; ~80 credits / second).
export const COST_SFX_PER_EFFECT_IN_CREDITS = 400;

// Podcast Clipper, per generated clip. Cost varies by length & complexity, so the
// pricing page shows a range: a cheap clip floors around the min, a heavy one around
// the max. Fewer clips at the max cost, more at the min.
export const COST_PODCAST_CLIP_MIN_IN_CREDITS = 10_000;
export const COST_PODCAST_CLIP_MAX_IN_CREDITS = 18_000;

// Speech separation, per minute of audio (cached re-use is free).
export const COST_SPEECH_SEPARATION_PER_MINUTE_IN_CREDITS = 100;

// Library search: 1 credit per file/transcript/visual search, 5 per unified search.
export const COST_SEARCH_PER_SEARCH_IN_CREDITS = 1;

// Rough minutes per finished render, to estimate how many no-watermark renders a plan covers.
export const MINUTES_PER_RENDER_ESTIMATE = 2;

// Default parameter for how much negative a user can go.
export const MINIMUM_CREDITS_ALLOWED = -5_000;

// Number of seconds in a minute.
export const SECONDS_IN_MINUTE = 60;

// Number of minutes in an hour multiplied by 60.
export const SECONDS_IN_HOUR = 3600;

// Maximum time to wait for a fetch operation to complete (in milliseconds)
export const FETCH_TIMEOUT_MS = 5000;

// localStorage keys for billing data
const BILLING_CREDITS_KEY = 'billing.creditsLeft';
const BILLING_STORAGE_KEY = 'billing.storageLeft';
const BILLING_USER_EMAIL_KEY = 'billing.userEmail';
const BILLING_LAST_UPDATED_KEY = 'billing.lastUpdated';

// TODO: But not on this branch / PR: Have a user-readable "last updated" time.

export type Quota = {
  credits: number;
  creditsLifetimeDays: number;
  storageHours: number;
};

// null means no subscription is attached to the billing account. Whether an attached one
// entitles the user is `Billing.isFree`, never the presence of this object: the backend keeps
// a lapsed subscription (`unpaid`, `paused`, `incomplete`) attached so the user can repair it
// in the Stripe portal, and only drops it once Stripe reports it `canceled` or expired.
export type Subscription = null | {
  id: string;
  // The backend resolves the Stripe product at read time and sends null when that lookup
  // finds nothing, so a subscription can arrive without its plan.
  product: Product | null;
  status:
    | 'active'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
    | 'past_due'
    | 'paused'
    | 'trialing'
    | 'unpaid';
  createdAt: number;
  termStart: number;
  termEnd: number;
  quota: Quota;
};

export type CreditNoteOrigin = 'subscription' | 'one-time-purchase' | 'signup';
export type UsageHistoryType =
  | 'sz.ingest'
  | 'sz.store'
  | 'sz.search'
  | 'sz.search.unified'
  | 'sz.render';

export type UsageHistoryAction = {
  type: UsageHistoryType;
  credits: number;
  value: number;
};

export type UsageHistoryResponse = Array<UsageHistory>;

export type UsageHistory = {
  _id: string;
  createdAt: number;
  updatedAt: number;
  billingId: string;
  invoiceId: string;
  sessionId: string;
  initiator: string;
  type: string;
  resourceId: string;
  actions: UsageHistoryAction[];
  creditsUsed: number;
};

export type CreditNote = {
  _id: string;
  billingId: string;
  invoiceId: string;
  sessionId: string;
  subscriptionId: string;
  expiresAt: number;
  expired: boolean;
  origin: CreditNoteOrigin;
  credits: {
    originalAmount: number;
    currentAmount: number;
    expiredAmount: number;
  };
  createdAt: number;
  updatedAt: number;
};

export type UserSpecificLimitedValues = {
  storageHours: number;
};

export type Billing = {
  subscription: Subscription | null;
  /**
   * Plan flags computed by the backend from the subscription status, and the only reading of
   * "is this account subscribed" the frontend makes (see `resolvePlanAccess`).
   *
   * `isFree` is true when nothing entitles the account: no subscription, or one whose status
   * is not `active`, `trialing` or `past_due`. `isPastDue` is the dunning grace period after a
   * failed renewal — still entitled, but the user should fix their card. The same three flags
   * ride on `GET /user/me`, where the backend caches them for ~30s; this response is fresh.
   */
  isFree: boolean;
  isTrialing: boolean;
  isPastDue: boolean;
  limits?: UserSpecificLimitedValues;
  usage?: UserSpecificLimitedValues;
  creditsLeft: number;
  creditNotes: CreditNote[];
};

export type PurchaseResponse = {
  sessionId: string;
  redirectUrl: string;
};

/**
 * A plan change is made in place on Stripe, unless the current plan was granted without a card:
 * then the backend replaces it through Checkout and `checkout` is the session to send the user to.
 */
export type ChangeSubscriptionResponse = {
  checkout: PurchaseResponse | null;
};

export type Price = {
  id: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
};

export type Product = {
  id: string;
  name: string;
  description: string | null;
  tagline: string | null;
  images: string[];
  price: Price;
  quota: {
    credits: number;
    creditsLifetimeDays: number;
    storageHours: number;
  };
};

/**
 * Billing API calls with consistent error handling.
 * DO NOT STRINGIFY BODY INTO JSON SO THAT IT WORKS WITH MSGPACKR!
 */
export async function billingApi<T>(endpoint: string, options?: ApiRequestInit): Promise<T> {
  try {
    return await userStore.apiWithAuth<T>(`/billing${endpoint}`, options);
  } catch (error) {
    console.error(`Billing API error (${endpoint}):`, error);
    throw error;
  }
}

export class BillingStore extends DataFrame<Billing> {
  lastUpdate: number;
  isFetching: boolean;
  lastFetchTime: number;
  private cachedPortalUrl: string | null = null;
  private fetchPromise: Promise<Billing> | null = null;

  // Debug overrides — survive API refetches. Set via Debug tab.
  // Only active when debug mode is on (checked via localStorage to avoid circular imports).
  // Initialized via extendObservable in constructor (not field initializers) because
  // esnext class fields use Object.defineProperty which overwrites the MobX observable
  // set up by makeObservable(this) in the parent DataFrame constructor.
  debugCreditsOverride!: number | null;
  debugStorageLeftOverride!: number | null;

  private get isDebugMode() {
    return localStorage.getItem('respectfulDebugMode') === 'true';
  }

  usageHistoryData: {
    lastUpdate: number;
    loadedUpTo: number;
    data: Record<string, UsageHistory>;
  };

  // Observable (see the constructor) so `availableSubscriptions` recomputes when the plans
  // land: the paywall renders from it while the request is still in flight.
  availableSubscriptionsData!: {
    lastUpdate: number;
    data: Record<string, Product>;
  };

  availableOneTimeProductsData: {
    lastUpdate: number;
    data: Record<string, Product>;
  };

  constructor() {
    super({ autoFetch: 'nextTick' });
    extendObservable(this, {
      debugCreditsOverride: null,
      debugStorageLeftOverride: null,
      availableSubscriptionsData: {
        lastUpdate: 0,
        data: {},
      },
    });
    this.usageHistoryData = {
      lastUpdate: 0,
      loadedUpTo: Infinity,
      data: {},
    };
    this.availableOneTimeProductsData = {
      lastUpdate: 0,
      data: {},
    };
    this.lastUpdate = 0;
    this.lastFetchTime = 0;
    this.isFetching = false;

    // Cleanup when user logs out to prevent memory leaks
    reaction(
      () => userStore.isLoggedIn,
      (isLoggedIn) => {
        if (!isLoggedIn) {
          this.dispose();
        }
      },
      { fireImmediately: true },
    );

    // An unverified account has no billing document, so the boot-time fetch 404s and
    // leaves `error` set. Once the email is verified that error is stale, and the paywall
    // gate would read "billing failed" and flash its retry screen before WithUser's own
    // prefetch effect gets to run. Clear it and refetch here, synchronously with the
    // verification, so the first render after it sees "loading" instead.
    reaction(
      () => userStore.data?.isVerified,
      (isVerified) => {
        if (!isVerified) return;
        runInAction(() => {
          this.error = null;
        });
        this.fetch().catch((error) => {
          console.error('BillingStore: failed to fetch billing after verification:', error);
        });
      },
    );
  }

  @computed
  get creditsLeft() {
    // DEBUG ONLY: return doctored value from Debug tab when active
    if (this.debugCreditsOverride !== null && this.isDebugMode) return this.debugCreditsOverride;
    if (!this.data) return null;
    return this.data.creditsLeft;
  }

  @computed
  /* Estimate how many seconds of ingest are left for the user's credits. */
  get ingestLeft() {
    if (this.creditsLeft === null) return null;
    return Math.floor(
      (this.creditsLeft * SECONDS_IN_HOUR) / COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS,
    );
  }

  /**
   * Billing account the current user's own credits and storage live on — the id a library's
   * `billingId` is compared against (see `resolveBillingOwnership`).
   *
   * Prefers `/user/me`'s `billingId`, but does not depend on it: that field is **not** in the
   * endpoint's `required` set, and the accounts most likely to omit it are the org/SSO ones
   * that upload into other people's libraries — i.e. exactly the cohort the cross-billing
   * warning exists for. Without a fallback, those users get `unknown` for every library and the
   * feature silently no-ops. `/billing/info` describes *this* user's billing account, and its
   * credit notes carry a required `billingId`, so they answer the same question.
   *
   * The fallback only speaks when **every** credit note agrees on one id. A history spanning
   * two billing accounts would otherwise let a stale id decide the comparison, and a wrong
   * `'other'` verdict tells someone their upload is on a stranger's tab when it isn't — the one
   * outcome `resolveBillingOwnership` is deliberately built to avoid. Disagreement, or no notes
   * at all, returns `undefined`, which resolves to `'unknown'` and keeps every surface silent.
   *
   * Note the `DataFrame` caveat that applies to any read of `this.data`: a single failed
   * `/billing/info` fetch nulls it (`keepDataOnFetchError` is false), so this getter can go
   * from an answer back to `undefined`. Fine here — losing it costs a warning, never adds one.
   */
  @computed
  get currentBillingId(): string | undefined {
    return resolveUserBillingId(
      userStore.data?.billingId,
      (this.data?.creditNotes ?? []).map((creditNote) => creditNote?.billingId),
    );
  }

  @computed
  get creditNotesSortedByExpiryDate() {
    if (!this.data || this.data.creditNotes === undefined) return [];
    return [...this.data.creditNotes].sort((a, b) => a.expiresAt - b.expiresAt);
  }

  @computed
  get initialUnexpiredCreditNotes() {
    if (!this.data || this.data.creditNotes === undefined) return 0;
    return this.data.creditNotes
      .filter((creditNote) => !creditNote.expired)
      .reduce((acc, creditNote) => acc + creditNote.credits.originalAmount, 0);
  }

  @computed
  get creditsRemainingFromUnexpiredCreditNotes() {
    if (!this.data || this.data.creditNotes === undefined) return 0;
    return this.data.creditNotes
      .filter((creditNote) => !creditNote.expired)
      .reduce((acc, creditNote) => acc + creditNote.credits.currentAmount, 0);
  }

  /**
   * When the oldest unexpired credit note was issued: the start of the usage window. A
   * subscription note outlives its period by ~15 days, so for part of every month this reaches
   * into the previous one. Null when no note is unexpired.
   *
   * FIXME(credits-usage-gap): once that previous note expires the window jumps to the current
   * one, and spend drawn from the previous note stays in the ledger range but leaves the window's
   * notes. See `CreditUsageStatsStore` for the sizing and what retires it.
   */
  @computed
  get usageWindowStart(): number | null {
    const createdAts = (this.data?.creditNotes ?? [])
      .filter((creditNote) => !creditNote.expired)
      .map((creditNote) => creditNote.createdAt);
    return createdAts.length > 0 ? Math.min(...createdAts) : null;
  }

  /**
   * Every credit note issued since `usageWindowStart`, expired ones included (listed after the
   * unexpired ones, each group newest first): a short-lived note that ran out inside the window
   * still paid for spend the usage stats count.
   */
  @computed
  get usageWindowCreditNotes(): CreditNote[] {
    const since = this.usageWindowStart;
    if (since === null) return [];
    return (
      (this.data?.creditNotes ?? [])
        .filter((creditNote) => creditNote.createdAt >= since)
        // Unexpired first, then newest first within each group.
        .sort((a, b) => Number(a.expired) - Number(b.expired) || b.createdAt - a.createdAt)
    );
  }

  /** Credits the window's notes made spendable: what they granted minus what expired unused. */
  @computed
  get usageWindowSpendableCredits(): number {
    return this.usageWindowCreditNotes.reduce(
      (acc, creditNote) =>
        acc + creditNote.credits.originalAmount - creditNote.credits.expiredAmount,
      0,
    );
  }

  /** Credits the window's notes let expire before they were spent. */
  @computed
  get usageWindowExpiredCredits(): number {
    return this.usageWindowCreditNotes.reduce(
      (acc, creditNote) => acc + creditNote.credits.expiredAmount,
      0,
    );
  }

  @computed
  get creditUsageFromUnexpiredCreditNotes() {
    if (!this.data) return 0;
    return this.initialUnexpiredCreditNotes - this.creditsRemainingFromUnexpiredCreditNotes;
  }

  @computed
  /* Estimate how much storage is left in seconds. */
  get storageLeft() {
    // DEBUG ONLY: return doctored value from Debug tab when active
    if (this.debugStorageLeftOverride !== null && this.isDebugMode)
      return this.debugStorageLeftOverride;
    if (!this.data) return 0;
    if (this.data.limits === undefined || this.data.usage === undefined) return 0;
    return SECONDS_IN_HOUR * (this.data.limits.storageHours - this.data.usage.storageHours);
  }

  @computed
  /**
   * Current storage usage in seconds.
   *
   * DEBUG ONLY: This getter exists because the debug override fakes storageLeft (a derived
   * value: limits - usage) without touching data.usage. The plan coverage check needs
   * actual usage to compute totalStorageNeeded. Reading data.usage.storageHours directly
   * would ignore the override, making every plan look sufficient even with storage doctored
   * to -200h. This getter derives a consistent usage figure from the override so the plan
   * check produces realistic results. Without debug overrides it returns the real value.
   */
  get storageUsageSeconds() {
    if (!this.data) return 0;
    const limits = this.data.limits?.storageHours ?? 0;
    if (this.debugStorageLeftOverride !== null && this.isDebugMode) {
      // Derive usage from: storageLeft = (limits - usage) * 3600
      // => usage = limits * 3600 - storageLeft
      return limits * SECONDS_IN_HOUR - this.debugStorageLeftOverride;
    }
    return (this.data.usage?.storageHours ?? 0) * SECONDS_IN_HOUR;
  }

  hasEnoughCreditsWithTolerance(
    credits: number | null,
    cost: number,
    tolerance: number = MINIMUM_CREDITS_ALLOWED,
  ): boolean {
    if (credits === null) return false;
    return credits - tolerance >= cost;
  }

  hasEnoughCredits(credits: number | null, cost: number): boolean {
    if (credits === null) return false;
    return this.hasEnoughCreditsWithTolerance(credits, cost, 0);
  }

  async fetch() {
    // Return existing promise if already fetching
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    if (!userStore.token) {
      this.fetchPromise = null;
      return;
    }

    this.isFetching = true;
    this.lastFetchTime = Date.now();

    this.fetchPromise = userStore
      .apiWithAuth<Billing>('/billing/info')
      .then((billing) => {
        runInAction(() => {
          this.data = billing;
          this.error = null;
          this.lastUpdate = Date.now();
        });
        // Save to localStorage for cross-tab sync
        this.saveBillingToLocalStorage();
        return billing;
      })
      .catch((error: Error) => {
        // Record the failure: callers that go through this method directly (the WithUser
        // prefetch, every post-action refetch) otherwise leave `error` null, and the
        // bootstrap gate reads `!data && !error` as "still loading" — forever. The last
        // good `data` is kept on purpose: a refetch that fails mid-session must not empty
        // the credits box or flip the upload gate to "not enough credits".
        runInAction(() => {
          this.error = error;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.isFetching = false;
          this.fetchPromise = null;
        });
      });

    return this.fetchPromise;
  }

  /**
   * Ensures billing data is loaded. If data is already present, returns it immediately.
   * If not, fetches the data from the server.
   */
  async ensureLoaded() {
    if (this.data) {
      return this.data;
    }

    // Check if we're currently fetching to avoid duplicate calls
    if (this.isFetching) {
      // Wait for the current fetch to complete with timeout protection
      const startTime = Date.now();
      while (this.isFetching && Date.now() - startTime < FETCH_TIMEOUT_MS) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (!this.data) {
        throw new Error('Billing data failed to load within timeout');
      }
      return this.data;
    }

    return await this.fetch();
  }

  async fetchUsageHistory(limit: number = 10, olderThan?: number) {
    if (!userStore.token) return;

    const olderThanQuery =
      olderThan === undefined && this.usageHistoryData.loadedUpTo !== Infinity
        ? this.usageHistoryData.loadedUpTo
        : olderThan;

    const usageHistory = await billingApi<UsageHistoryResponse>(
      `/credits/usage/history?limit=${limit}${olderThanQuery ? `&olderThan=${olderThanQuery}` : ''}`,
    );
    runInAction(() => {
      usageHistory.forEach((usage) => {
        this.usageHistoryData.data[usage._id] = usage;
        this.usageHistoryData.loadedUpTo = Math.min(
          this.usageHistoryData.loadedUpTo,
          usage.updatedAt,
        );
      });
      this.usageHistoryData.lastUpdate = Date.now();
    });
  }

  /* Returns a session ID and redirect URL to the payment portal. */
  async purchase300kCredits(quantity: number): Promise<PurchaseResponse | undefined> {
    if (!userStore.token) return;

    return billingApi<PurchaseResponse>('/one-time/purchase', {
      method: 'POST',
      body: { quantity },
    });
  }

  /* Returns a session ID and redirect URL to the payment portal for a one-time product. */
  async purchaseOneTimeProduct(productId: string): Promise<PurchaseResponse | undefined> {
    if (!userStore.token) return;

    return billingApi<PurchaseResponse>('/one-time/purchase', {
      method: 'POST',
      body: { productId },
    });
  }

  async purchaseSubscription(productId: string): Promise<PurchaseResponse | undefined> {
    if (!userStore.token) return;

    return billingApi<PurchaseResponse>('/subscription/purchase', {
      method: 'POST',
      body: { productId },
    });
  }

  @computed
  get availableSubscriptions(): Product[] {
    return Object.values(this.availableSubscriptionsData.data);
  }

  /** Ensures subscription data is cached. Safe to call multiple times. */
  async ensureSubscriptionsLoaded(): Promise<void> {
    if (this.availableSubscriptionsData.lastUpdate > 0) return;
    await this.getAvailableSubscriptions();
  }

  async getAvailableSubscriptions(): Promise<Product[]> {
    // No user token required.

    try {
      const result = await api<{ products: Product[] }>('/billing/subscription/available');
      runInAction(() => {
        result.products.forEach((product) => {
          this.availableSubscriptionsData.data[product.id] = product;
        });
        this.availableSubscriptionsData.lastUpdate = Date.now();
      });
      return result.products;
    } catch (error) {
      console.error('Error getting available subscriptions:', error);
      throw error;
    }
  }

  async getAvailableOneTimeProducts(): Promise<Product[]> {
    const result = await api<{ products: Product[] }>('/billing/one-time/available');
    return result.products;
  }

  async changeSubscription(productId: string): Promise<ChangeSubscriptionResponse | undefined> {
    if (!userStore.token) return;

    return billingApi<ChangeSubscriptionResponse>('/subscription/change', {
      method: 'POST',
      body: { productId },
    });
  }

  async getPortalUrl(): Promise<string | undefined> {
    if (!userStore.token) return;

    // Return cached URL if available
    if (this.cachedPortalUrl) {
      return this.cachedPortalUrl;
    }

    // Fetch and cache the portal URL
    const data = await userStore.apiWithAuth<{ url: string }>('/billing/portal');
    this.cachedPortalUrl = data.url;
    return data.url;
  }

  async getInvoiceUrl(creditNoteId: string): Promise<string | undefined> {
    if (!userStore.token) return;
    const result = await userStore.apiWithAuth<{ invoiceUrl: string }>(
      `/billing/invoice/${creditNoteId}/url`,
    );
    return result.invoiceUrl;
  }

  /**
   * Safely refetches billing data after operations that affect credits.
   * Includes consistent error handling to prevent crashes from network errors.
   * Shows user-friendly error toast on failure.
   */
  // Despite the name, this refetches ALL billing data (credits, storage limits, usage)
  // via forceRefreshBillingData → fetch → /billing/info. The name is historical.
  async refetchCreditsAfterOperation(): Promise<void> {
    if (!userStore.data?.isVerified) {
      // If user is not yet verified, it's normal that credits wont refresh.
      // Useless to show an error when it is expected.
      return;
    }
    try {
      await this.forceRefreshBillingData();
    } catch (error) {
      // Show user-friendly error toast
      showToast('credits-refresh-error', { error });
    }
  }

  /**
   * Force refresh billing data, bypassing throttle for immediate updates after subscription changes
   */
  async forceRefreshBillingData(): Promise<void> {
    // Clear any existing promise to force a fresh fetch
    this.fetchPromise = null;
    await this.fetch();
  }

  /**
   * Save billing data to localStorage for cross-tab synchronization
   * Stores credits, storage, user email, and timestamp
   */
  saveBillingToLocalStorage(): void {
    if (!this.data || !userStore.data?.email) {
      return;
    }

    try {
      const now = Date.now();

      localStorage.setItem(BILLING_CREDITS_KEY, String(this.data.creditsLeft));

      // Calculate and save storage left
      const storageLeft = this.storageLeft;
      localStorage.setItem(BILLING_STORAGE_KEY, String(storageLeft));

      // Save user email as a safety check
      localStorage.setItem(BILLING_USER_EMAIL_KEY, userStore.data.email);

      // Save timestamp for staleness checking
      localStorage.setItem(BILLING_LAST_UPDATED_KEY, String(now));
    } catch (error) {
      console.error('Failed to save billing data to localStorage:', error);
    }
  }

  /**
   * Load billing data from localStorage for cross-tab sync.
   * Only syncs creditsLeft — storage is skipped because storageLeft is derived from
   * limits and usage, and saving/loading both would be more complex. This means
   * background tabs won't pick up storage changes (e.g. after a plan upgrade) until
   * their next full API refetch. The active tab (where Stripe redirects back to)
   * always gets the full refresh via refetchCreditsAfterOperation.
   */
  loadBillingFromLocalStorage(): boolean {
    if (!userStore.data?.email) {
      return false;
    }

    try {
      const savedEmail = localStorage.getItem(BILLING_USER_EMAIL_KEY);
      const savedCredits = localStorage.getItem(BILLING_CREDITS_KEY);
      const savedStorage = localStorage.getItem(BILLING_STORAGE_KEY);

      // Safety check: ensure the saved data is for the current user
      if (savedEmail !== userStore.data.email) {
        console.warn('Billing localStorage email mismatch. Clearing stale data.');
        this.clearBillingFromLocalStorage();
        return false;
      }

      // If we have saved data, update the store
      if (savedCredits !== null && this.data) {
        const creditsLeft = parseFloat(savedCredits);
        if (!isNaN(creditsLeft)) {
          runInAction(() => {
            if (this.data) {
              // Create a new object to trigger MobX reactivity
              this.data = {
                ...this.data,
                creditsLeft,
              };
            }
          });
        }
      }

      // Note: We save storageLeft but don't load it back into the store
      // because it's a computed value from limits and usage.
      // We keep it in localStorage for informational purposes only.

      return true;
    } catch (error) {
      console.error('Failed to load billing data from localStorage:', error);
      return false;
    }
  }

  /**
   * Get the last updated timestamp from localStorage
   * Returns null if not found or invalid
   */
  getLastUpdatedFromLocalStorage(): number | null {
    try {
      const lastUpdated = localStorage.getItem(BILLING_LAST_UPDATED_KEY);
      if (lastUpdated) {
        const timestamp = parseInt(lastUpdated, 10);
        return isNaN(timestamp) ? null : timestamp;
      }
      return null;
    } catch (error) {
      console.error('Failed to get last updated from localStorage:', error);
      return null;
    }
  }

  /**
   * Check if billing data in localStorage is stale (older than specified time)
   * @param maxAgeMs Maximum age in milliseconds (default: 60000ms = 1 minute)
   */
  isBillingDataStale(maxAgeMs: number = 60000): boolean {
    const lastUpdated = this.getLastUpdatedFromLocalStorage();
    if (lastUpdated === null) {
      return true; // No timestamp means stale
    }
    const age = Date.now() - lastUpdated;
    return age > maxAgeMs;
  }

  /**
   * Clear billing data from localStorage
   * Should be called on logout
   */
  clearBillingFromLocalStorage(): void {
    try {
      localStorage.removeItem(BILLING_CREDITS_KEY);
      localStorage.removeItem(BILLING_STORAGE_KEY);
      localStorage.removeItem(BILLING_USER_EMAIL_KEY);
      localStorage.removeItem(BILLING_LAST_UPDATED_KEY);
    } catch (error) {
      console.error('Failed to clear billing data from localStorage:', error);
    }
  }

  /**
   * Cleanup method to prevent memory leaks
   * Clears any pending operations and resets state
   */
  dispose(): void {
    this.fetchPromise = null;
    this.isFetching = false;
  }
}

/**
 * Ask the backend whether *the library's* billing account can cover `nSeconds` of ingest.
 *
 * A free function, not a `BillingStore` method: it holds no state and describes an account that
 * is not this store's — `BillingStore.data` is `GET /billing/info`, scoped to the signed-in user.
 * It sits next to `computeBillingImpact`, its counterpart for the user's own balance.
 *
 * Resolves rather than throws, always, so no caller has to remember to fail open. The 200 body is
 * the cost, which `estimateIngest` already computes, so only the failure is read.
 *
 * `nSeconds` is `IngestEstimate.durationSeconds`, the selection's real length. The endpoint
 * checks storage and credits against that one number, and storage counts real length, so a
 * per-item-floored total would report a storage shortfall that isn't there. The credit half
 * floors and rounds the total once, where ingest does it per media, so for a batch of images or
 * short clips it answers low and misses a shortfall, which atlas then refuses at ingest.
 * Exact needs atlas to accept per-item lengths here.
 */
export async function simulateLibraryIngest(
  libraryId: string,
  nSeconds: number,
  options?: { signal?: AbortSignal },
): Promise<OwnerAffordability> {
  const ingestSeconds = Math.max(0, Math.ceil(nSeconds));
  if (!libraryId || ingestSeconds <= 0) return AFFORDABILITY_UNKNOWN;
  try {
    await userStore.apiWithAuth<{ ingest: { credits: number; videoSeconds: number } }>(
      `/billing/simulate/library/${encodeURIComponent(libraryId)}/ingest`,
      { method: 'POST', body: { ingestSeconds }, signal: options?.signal },
    );
    return { state: 'affordable' };
  } catch (error) {
    // An abort is not a verdict, and returning early keeps it out of the logs below.
    if (options?.signal?.aborted) return AFFORDABILITY_UNKNOWN;
    const verdict = affordabilityFromError(error);
    if (verdict.state === 'unknown') {
      // Not a 402, so not an answer about the balance. It fails open either way, but silently
      // switching the pre-check off for every cross-billed upload should leave a trace.
      console.error(`Owner affordability probe failed (library ${libraryId}):`, error);
    }
    return verdict;
  }
}

export function computeBillingImpact({ credits: creditCost, durationSeconds }: IngestEstimate) {
  const creditsLeft = billingStore.creditsLeft;
  const creditsAfterProcessing =
    creditsLeft !== null ? Math.max(creditsLeft - creditCost, -1) : null;
  const storageLeft = billingStore.storageLeft;
  const storageAfterProcessing = Math.max(storageLeft - durationSeconds, -1);
  return { creditCost, creditsLeft, creditsAfterProcessing, storageLeft, storageAfterProcessing };
}

export function approximateNumberOfSecondsOfMediaLeft(nCredits: number): number {
  if (nCredits <= 0) {
    return 0;
  }
  return Math.ceil((nCredits / COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS) * SECONDS_IN_HOUR);
}

// singleton, as we don't need multiple instances of users
// also simplifies our life with api calls from other stores
export const billingStore = new BillingStore();

// @ts-ignore
window.billingStore = billingStore;
